import { useState } from "react"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Switch } from "@/components/ui/switch"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Settings } from "lucide-react"
import { useAuth } from "@/context/auth/auth-context"
import { updatePortfolioSettings, updateCashSettings } from "@/lib/api/hive"
import type { Portfolio } from "@/types/portfolio"

interface PortfolioSettingsPanelProps {
	portfolio: Portfolio
	onUpdated: (portfolio: Portfolio) => void
	onCashToggled?: () => void
}

export function PortfolioSettingsPanel({ portfolio, onUpdated, onCashToggled }: PortfolioSettingsPanelProps) {
	const { token } = useAuth()
	const [dripDefault, setDripDefault] = useState<"ON" | "OFF">(portfolio.drip_default ?? "OFF")
	const [buyFee, setBuyFee] = useState(String(portfolio.buy_fee ?? ""))
	const [buyFeeFlat, setBuyFeeFlat] = useState(String(portfolio.buy_fee_flat ?? ""))
	const [dripIncursFee, setDripIncursFee] = useState(portfolio.drip_incurs_fee ?? false)
	const [taxRate, setTaxRate] = useState(String(portfolio.tax_withholding_rate ?? ""))
	const [cashEnabled, setCashEnabled] = useState(portfolio.cash_tracking_enabled ?? false)
	const [saving, setSaving] = useState(false)
	const [error, setError] = useState<string | null>(null)

	const handleCashToggle = async (newValue: boolean) => {
		setCashEnabled(newValue)
		try {
			await updateCashSettings(portfolio.id, newValue)
			onCashToggled?.()
		} catch {
			setCashEnabled(!newValue)
		}
	}

	const handleSave = async () => {
		if (!token) return
		setSaving(true)
		setError(null)
		try {
			const updated = await updatePortfolioSettings(
				portfolio.id,
				{
					drip_default: dripDefault,
					buy_fee: buyFee ? parseFloat(buyFee) : undefined,
					buy_fee_flat: buyFeeFlat ? parseFloat(buyFeeFlat) : undefined,
					drip_incurs_fee: dripIncursFee,
					tax_withholding_rate: taxRate ? parseFloat(taxRate) : undefined,
				},
				token,
			)
			onUpdated(updated)
		} catch (err) {
			setError(err instanceof Error ? err.message : "Failed to save settings")
		} finally {
			setSaving(false)
		}
	}

	return (
		<Card className="bg-white/60 dark:bg-black/20 backdrop-blur-md border-sky-200/50 dark:border-sky-800/30">
			<CardHeader>
				<CardTitle className="text-base flex items-center gap-2">
					<Settings className="h-4 w-4" />
					Portfolio Settings
				</CardTitle>
			</CardHeader>
			<CardContent className="space-y-4">
				<div className="flex items-center gap-3">
					<Switch checked={cashEnabled} onCheckedChange={handleCashToggle} />
					<Label>Enable cash tracking</Label>
				</div>

				<div className="space-y-1.5">
					<Label>Default DRIP</Label>
					<Select value={dripDefault} onValueChange={(v) => setDripDefault(v as "ON" | "OFF")}>
						<SelectTrigger className="w-full">
							<SelectValue />
						</SelectTrigger>
						<SelectContent>
							<SelectItem value="ON">DRIP On</SelectItem>
							<SelectItem value="OFF">DRIP Off</SelectItem>
						</SelectContent>
					</Select>
					<p className="text-xs text-muted-foreground">Positions set to "Inherit" will use this default</p>
				</div>

				<div className="grid grid-cols-2 gap-4">
					<div className="space-y-1.5">
						<Label>Buy Fee (%)</Label>
						<Input
							type="text"
							inputMode="decimal"
							value={buyFee}
							onChange={(e) => setBuyFee(e.target.value)}
							placeholder="0"
						/>
					</div>
					<div className="space-y-1.5">
						<Label>Buy Fee (flat)</Label>
						<Input
							type="text"
							inputMode="decimal"
							value={buyFeeFlat}
							onChange={(e) => setBuyFeeFlat(e.target.value)}
							placeholder="0"
						/>
					</div>
				</div>
				<p className="text-xs text-muted-foreground -mt-2">Flat fee is in the stock's trading currency</p>

				<div className="flex items-center gap-3">
					<Switch checked={dripIncursFee} onCheckedChange={setDripIncursFee} />
					<Label>DRIP reinvestments incur buy fee</Label>
				</div>

				<div className="space-y-1.5">
					<Label>Tax Withholding Rate (%)</Label>
					<Input
						type="text"
						inputMode="decimal"
						value={taxRate}
						onChange={(e) => setTaxRate(e.target.value)}
						placeholder="0"
						className="w-32"
					/>
					<p className="text-xs text-muted-foreground">
						Default withholding rate for positions in this portfolio
					</p>
				</div>

				{error && <p className="text-sm text-destructive">{error}</p>}

				<Button onClick={handleSave} disabled={saving} className="bg-sky-500 hover:bg-sky-600 text-white">
					{saving ? "Saving..." : "Save Settings"}
				</Button>
			</CardContent>
		</Card>
	)
}
